import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Search, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';

/**
 * iter74 — One-click production cleanup, gated to platform admin.
 *
 * Why this page exists:
 * The CLI cleanup script in `backend/scripts/cleanup_live_data.py`
 * requires `mongodump` + Python + production DB credentials on the
 * operator's local machine. That's friction. This page lets a
 * platform admin run the exact same cleanup straight from the browser
 * after logging in to /admin — no local setup required.
 *
 * Safety layers (all backed by /api/admin/live-cleanup/*):
 *   1. Platform-admin gate (server-side)
 *   2. DRY-RUN is the default — operator must explicitly load the plan
 *   3. Operator picks the family keeper (Riyadh) by typing/searching
 *   4. Operator can add multiple extra keepers (e.g. Fady Daniel)
 *   5. Plan shows EXACTLY what will be kept (with all 16 demos by name)
 *      and what will be deleted (with per-collection counts)
 *   6. Execute requires typing the dynamic confirmation phrase
 *      "DELETE EVERYTHING EXCEPT <KEEPER_FIRST_NAME>" (server-validated)
 *   7. Every affected collection is snapshotted into
 *      `_cleanup_backup_<TIMESTAMP>_<coll>` inside the same DB BEFORE
 *      any deletes happen — so the admin can rollback by restoring
 *      from those backup collections at any time.
 */
export default function AdminLiveCleanup() {
  const [partners, setPartners] = useState([]);
  const [partnerQ, setPartnerQ] = useState('');
  const [keepIds, setKeepIds] = useState([]);   // [primary, ...extras]
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [results, setResults] = useState(null);
  // iter75 — Always-preserved list shown BEFORE the operator picks a
  // keeper so they know the admins + demos are safe regardless.
  const [alwaysKept, setAlwaysKept] = useState(null);

  // Pull the partner list once so the operator can pick e.g. Fady Daniel.
  const reloadPartners = (q) => {
    api.get('/admin/live-cleanup/partners', { params: q ? { q } : {} })
      .then(({ data }) => setPartners(Array.isArray(data?.partners) ? data.partners : []))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { reloadPartners(''); }, []);

  // Fetch the always-preserved list (admins + 16 demos) on mount so
  // the operator has visual proof BEFORE picking anyone. Calls the
  // plan endpoint with no keepers — server still returns the
  // automatic preserves.
  useEffect(() => {
    api.post('/admin/live-cleanup/plan', { keep_user_ids: [] })
      .then(({ data }) => setAlwaysKept(data))
      .catch(() => {});
  }, []);

  const loadPlan = (ids) => {
    setLoadingPlan(true);
    setPlan(null);
    api.post('/admin/live-cleanup/plan', { keep_user_ids: ids })
      .then(({ data }) => setPlan(data))
      .catch((e) => toast.error(formatApiError(e, 'Could not build plan.')))
      .finally(() => setLoadingPlan(false));
  };

  const addKeeper = (uid) => {
    if (!uid || keepIds.includes(uid)) return;
    const next = [...keepIds, uid];
    setKeepIds(next);
    loadPlan(next);
  };
  const removeKeeper = (uid) => {
    const next = keepIds.filter((x) => x !== uid);
    setKeepIds(next);
    if (next.length) loadPlan(next);
    else setPlan(null);
  };

  const expected = plan?.expected_confirm_phrase || '';
  const canExecute = plan && plan.keeper
    && (confirmPhrase || '').trim().toUpperCase() === (expected || '').toUpperCase();

  const execute = async () => {
    if (!canExecute) return;
    setExecuting(true);
    try {
      const { data } = await api.post('/admin/live-cleanup/execute', {
        keep_user_ids: keepIds,
        confirm: true,
        confirm_phrase: confirmPhrase,
      });
      setResults(data);
      toast.success('Cleanup complete. Audit row written.');
    } catch (e) {
      toast.error(formatApiError(e, 'Cleanup failed.'));
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar onDark={false} />
      <AdminLayout>
        <header data-testid="cleanup-header">
          <p className="ml-overline text-[hsl(var(--accent))]">Platform admin</p>
          <h1 className="font-serif text-3xl text-[hsl(var(--primary))] mt-1">Live data cleanup</h1>
          <div className="mt-3 flex items-start gap-2 text-[12px] text-[hsl(var(--primary)/0.7)] max-w-2xl">
            <ShieldAlert size={16} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
            <span>
              This will <span className="font-semibold text-[hsl(var(--primary))]">permanently delete</span> every
              family, partner, application, override, engraving quote, plaque order,
              assistance request and tribute that doesn't belong to a keeper you select below.
              Platform admins and the 16 "Still Here" demo memorials are always preserved.
              A snapshot of every affected collection is written into
              <code className="text-[11px] bg-[hsl(var(--primary)/0.05)] px-1.5 rounded">_cleanup_backup_*</code>
              collections BEFORE any deletion — your data is recoverable for as long
              as you keep those backup collections.
            </span>
          </div>
        </header>

        {/* iter75 — Always-preserved reassurance banner.
            Visible BEFORE the operator picks any keeper so they know
            the admins + 16 demos are safe automatically. */}
        {alwaysKept && (
          <section
            className="rounded-2xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent)/0.06)] p-5"
            data-testid="cleanup-always-kept"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-base text-[hsl(var(--primary))]">
                  Always preserved automatically
                </h3>
                <p className="text-[11px] text-[hsl(var(--primary)/0.7)] mt-1">
                  You do not have to pick these — the server protects them on every cleanup.
                </p>
                <div className="mt-4 grid md:grid-cols-2 gap-5">
                  <div>
                    <div className="ml-overline text-[hsl(var(--primary)/0.7)]">
                      Platform admins ({(alwaysKept.keep_admin_details || []).filter(d => d.role === 'platform_admin').length})
                    </div>
                    <ul className="mt-2 space-y-1 text-[12px] text-[hsl(var(--primary)/0.85)]">
                      {(alwaysKept.keep_admin_details || [])
                        .filter(d => d.role === 'platform_admin')
                        .map(d => (
                          <li key={d.user_id} data-testid={`always-admin-${d.user_id}`}>
                            ✓ <span className="font-medium">{d.name || '(no name)'}</span>
                            {' '}&lt;{d.email}&gt;
                          </li>
                        ))}
                    </ul>
                  </div>
                  <div>
                    <div className="ml-overline text-[hsl(var(--primary)/0.7)]">
                      "Still Here" demo memorials ({(alwaysKept.keep_memorial_details_demo || []).length})
                    </div>
                    <ul className="mt-2 space-y-1 text-[12px] text-[hsl(var(--primary)/0.85)] max-h-[180px] overflow-y-auto pr-2">
                      {(alwaysKept.keep_memorial_details_demo || []).map(m => (
                        <li key={m.memorial_id} data-testid={`always-demo-${m.memorial_id}`}>
                          ✓ {m.full_name} <span className="text-[10px] text-[hsl(var(--primary)/0.55)]">/{m.slug}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="text-[10px] text-[hsl(var(--primary)/0.55)] mt-3">
                  Below, just pick the additional family + partner accounts (e.g. Riyadh, Fady) you also want to keep.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Section 1 — Pick keepers */}
        <section className="rounded-2xl border border-[hsl(var(--primary)/0.14)] bg-[hsl(var(--card))] overflow-hidden">
          <div className="px-6 py-4 border-b border-[hsl(var(--primary)/0.1)]">
            <h2 className="font-serif text-lg text-[hsl(var(--primary))]">Step 1 · Pick the accounts to keep</h2>
            <p className="text-[11px] text-[hsl(var(--primary)/0.6)] mt-1">
              Search by name, email, or business. Click to add. The first
              keeper is treated as the primary family account; additional
              keepers are surfaced separately in the plan.
            </p>
          </div>
          <div className="px-6 py-4 flex flex-wrap gap-2 border-b border-[hsl(var(--primary)/0.08)]">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--primary)/0.45)]" />
              <input
                value={partnerQ}
                onChange={(e) => setPartnerQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') reloadPartners(partnerQ); }}
                placeholder="Search partners (e.g. Fady Daniel)…"
                className="ml-input pl-9 w-full"
                data-testid="cleanup-partner-search"
              />
            </div>
            <button
              type="button"
              onClick={() => reloadPartners(partnerQ)}
              className="ml-btn-outline !py-2"
              data-testid="cleanup-partner-search-btn"
            >
              Search
            </button>
          </div>

          {/* Picked keepers */}
          {keepIds.length > 0 && (
            <div className="px-6 py-3 border-b border-[hsl(var(--primary)/0.08)] flex flex-wrap gap-2" data-testid="cleanup-selected">
              {keepIds.map((uid, i) => (
                <span
                  key={uid}
                  className={
                    'inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] border ' +
                    (i === 0
                      ? 'bg-[hsl(var(--accent)/0.12)] border-[hsl(var(--accent))] text-[hsl(var(--primary))]'
                      : 'bg-[hsl(var(--primary)/0.05)] border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))]')
                  }
                  data-testid={`cleanup-selected-${uid}`}
                >
                  {i === 0 ? 'Primary keeper · ' : 'Extra · '}{uid}
                  <button onClick={() => removeKeeper(uid)} className="text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--destructive))]">×</button>
                </span>
              ))}
            </div>
          )}

          <div className="max-h-[280px] overflow-y-auto">
            <ul className="divide-y divide-[hsl(var(--primary)/0.06)]" data-testid="cleanup-partner-list">
              {partners.slice(0, 200).map((p) => (
                <li key={p.user_id} className="px-6 py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[hsl(var(--primary))] font-medium truncate text-[13px]">
                      {p.name || '(no name)'}
                      {p.business_name && (
                        <span className="text-[11px] text-[hsl(var(--primary)/0.55)]"> · {p.business_name}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-[hsl(var(--primary)/0.6)] tabular-nums truncate">
                      {p.email || '—'} · {(p.account_type || '').replace('_', ' ')} · {p.user_id}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => addKeeper(p.user_id)}
                    disabled={keepIds.includes(p.user_id)}
                    className="ml-btn-outline !py-1.5 !text-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`cleanup-keep-${p.user_id}`}
                  >
                    {keepIds.includes(p.user_id) ? 'Kept' : 'Keep'}
                  </button>
                </li>
              ))}
              {partners.length === 0 && (
                <li className="px-6 py-6 text-center text-[12px] text-[hsl(var(--primary)/0.55)]">
                  No partners match this search. (To keep a non-partner like Riyadh, paste the user_id directly below.)
                </li>
              )}
            </ul>
          </div>

          <div className="px-6 py-3 border-t border-[hsl(var(--primary)/0.08)] flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="…or paste a user_id directly (e.g. Riyadh's account)"
              className="ml-input flex-1 min-w-[260px]"
              data-testid="cleanup-manual-uid"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const v = e.currentTarget.value.trim();
                  if (v) {
                    addKeeper(v);
                    e.currentTarget.value = '';
                  }
                }
              }}
            />
            <span className="text-[10px] text-[hsl(var(--primary)/0.55)]">Press Enter to add</span>
          </div>
        </section>

        {/* Section 2 — Plan preview */}
        <section className="rounded-2xl border border-[hsl(var(--primary)/0.14)] bg-[hsl(var(--card))] overflow-hidden">
          <div className="px-6 py-4 border-b border-[hsl(var(--primary)/0.1)] flex items-center justify-between">
            <div>
              <h2 className="font-serif text-lg text-[hsl(var(--primary))]">Step 2 · Review the plan</h2>
              <p className="text-[11px] text-[hsl(var(--primary)/0.6)] mt-1">
                Nothing is deleted until you type the confirmation phrase below.
              </p>
            </div>
            {loadingPlan && <Loader2 className="animate-spin text-[hsl(var(--primary)/0.55)]" size={18} />}
          </div>

          {!plan && !loadingPlan && (
            <div className="px-6 py-10 text-center text-[12px] text-[hsl(var(--primary)/0.55)]" data-testid="cleanup-plan-empty">
              Pick at least one keeper above to see the plan.
            </div>
          )}

          {plan && (
            <div className="grid md:grid-cols-2 gap-px bg-[hsl(var(--primary)/0.08)]" data-testid="cleanup-plan">
              <div className="bg-[hsl(var(--card))] p-6">
                <h3 className="ml-overline text-[hsl(var(--accent))]">Will be kept</h3>
                <div className="mt-3 space-y-3 text-[12px]">
                  {plan.keeper ? (
                    <div>
                      <div className="font-semibold text-[hsl(var(--primary))]">A · Primary family keeper</div>
                      <div>{plan.keeper.name || '(no name)'} &lt;{plan.keeper.email}&gt;</div>
                      <div className="text-[11px] text-[hsl(var(--primary)/0.6)]">{plan.keeper_memorials.length} memorial(s) owned</div>
                    </div>
                  ) : (
                    <div className="text-[hsl(var(--destructive))]">⚠ No primary keeper selected.</div>
                  )}
                  {plan.extra_keepers?.length > 0 && (
                    <div>
                      <div className="font-semibold text-[hsl(var(--primary))]">A2 · Additional keepers ({plan.extra_keepers.length})</div>
                      <ul className="list-disc ml-5 mt-1 space-y-1">
                        {plan.extra_keepers.map((ek) => (
                          <li key={ek.user_id} data-testid={`cleanup-plan-extra-${ek.user_id}`}>
                            <span className="font-medium">{ek.name || '?'}</span>
                            {' '}&lt;{ek.email}&gt;
                            {ek.business_name && <span className="text-[hsl(var(--primary)/0.6)]"> · {ek.business_name}</span>}
                            {ek.account_type && <span className="text-[10px] text-[hsl(var(--primary)/0.55)]"> · {ek.account_type.replace('_', ' ')}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-[hsl(var(--primary))]">B · Platform admins ({plan.keep_admin_details?.filter(d => d.role === 'platform_admin').length || 0})</div>
                    <ul className="list-disc ml-5 mt-1 space-y-0.5 text-[11px] text-[hsl(var(--primary)/0.7)]">
                      {(plan.keep_admin_details || []).filter(d => d.role === 'platform_admin').map(d => (
                        <li key={d.user_id}>{d.name || '?'} &lt;{d.email}&gt;</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold text-[hsl(var(--primary))]">D · Demo "Still Here" memorials ({plan.keep_memorial_details_demo?.length || 0})</div>
                    <ul className="list-disc ml-5 mt-1 space-y-0.5 text-[11px] text-[hsl(var(--primary)/0.7)] max-h-[160px] overflow-y-auto">
                      {(plan.keep_memorial_details_demo || []).map(m => (
                        <li key={m.memorial_id}>{m.full_name} · /{m.slug}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="bg-[hsl(var(--card))] p-6">
                <h3 className="ml-overline text-[hsl(var(--destructive))]">Will be deleted</h3>
                <div className="mt-3 text-[12px] space-y-2">
                  <div>
                    <span className="font-semibold text-[hsl(var(--destructive))] tabular-nums" data-testid="cleanup-delete-user-count">{plan.delete_user_count}</span> user accounts
                  </div>
                  <div>
                    <span className="font-semibold text-[hsl(var(--destructive))] tabular-nums" data-testid="cleanup-delete-memorial-count">{plan.delete_memorial_count}</span> memorials
                  </div>
                </div>
                <div className="mt-4 text-[11px] text-[hsl(var(--primary)/0.7)]">
                  <div className="font-semibold mb-1">Cascade counts</div>
                  <ul className="space-y-0.5 max-h-[260px] overflow-y-auto">
                    {Object.entries(plan.counts || {}).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                      <li key={k} className="flex items-center justify-between tabular-nums">
                        <span className="text-[hsl(var(--primary)/0.6)]">{k}</span>
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Section 3 — Execute */}
        {plan && plan.keeper && (
          <section className="rounded-2xl border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive)/0.04)] p-6 space-y-3" data-testid="cleanup-execute-panel">
            <h2 className="font-serif text-lg text-[hsl(var(--primary))]">Step 3 · Confirm and execute</h2>
            <p className="text-[12px] text-[hsl(var(--primary)/0.8)]">
              To proceed, type this phrase exactly:
              <code className="ml-2 px-2 py-0.5 rounded bg-[hsl(var(--card))] border border-[hsl(var(--primary)/0.18)] tabular-nums">
                {expected}
              </code>
            </p>
            <input
              type="text"
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder={expected}
              className="ml-input w-full font-mono"
              data-testid="cleanup-confirm-input"
              autoComplete="off"
            />
            <div className="flex gap-3 items-center">
              <button
                type="button"
                onClick={execute}
                disabled={!canExecute || executing}
                data-testid="cleanup-execute-btn"
                className="ml-btn-primary !bg-[hsl(var(--destructive))] !text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {executing ? 'Cleaning…' : `Delete ${plan.delete_user_count} accounts + ${plan.delete_memorial_count} memorials`}
              </button>
              <span className="text-[10px] text-[hsl(var(--primary)/0.55)]">
                A backup snapshot will be written first.
              </span>
            </div>
          </section>
        )}

        {/* Results */}
        {results && (
          <section className="rounded-2xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent)/0.05)] p-6" data-testid="cleanup-results">
            <h3 className="font-serif text-lg text-[hsl(var(--primary))]">Cleanup complete</h3>
            <p className="text-[12px] text-[hsl(var(--primary)/0.7)] mt-1">
              Backup snapshot stamp: <code className="bg-[hsl(var(--card))] px-2 py-0.5 rounded">{results.results?._backup_stamp || '—'}</code>
            </p>
            <pre className="mt-3 text-[11px] bg-[hsl(var(--card))] p-4 rounded-lg max-h-[300px] overflow-auto">
              {JSON.stringify(results.results, null, 2)}
            </pre>
          </section>
        )}
      </AdminLayout>
    </div>
  );
}
